/**
 * Validates local-library track/album references against the home SQLite catalog.
 * Used at queue + Recently Played hydration — never during per-track playback.
 */

import { useMaestroStore } from '../store/maestroStore';
import { libraryService } from './libraryService';
import { shouldUseLocalLibraryIpc, isHomeLibraryHost } from './localLibraryIpcGate';
import { isLocalLibraryTrack, isLocalTrackId } from '../lib/playback-bridge';
import { normalizeLocalAlbumId } from './localLibraryMetadata';
import type { RecentAlbum } from '../store/recentAlbumsStore';
import { getCachedLocalAlbumDetail } from './albumDetailCache';
import { purgeStaleLocalAlbumRef } from './staleLocalAlbumPurge';

export type LocalRefValidationStatus = 'valid' | 'missing_track' | 'missing_file_path';

const BATCH_SIZE = 50;
const CACHE_TTL_MS = 5 * 60 * 1000;

type CacheEntry = { status: LocalRefValidationStatus; at: number };
const trackStatusCache = new Map<string, CacheEntry>();
const albumStatusCache = new Map<string, CacheEntry>();

export function shouldValidateLocalLibraryRefs(): boolean {
  return shouldUseLocalLibraryIpc() && isHomeLibraryHost();
}

export function clearLocalLibraryValidationCache(): void {
  trackStatusCache.clear();
  albumStatusCache.clear();
}

function cacheTrackStatus(id: string, status: LocalRefValidationStatus) {
  trackStatusCache.set(id, { status, at: Date.now() });
}

function cacheAlbumStatus(id: string, status: LocalRefValidationStatus) {
  albumStatusCache.set(id, { status, at: Date.now() });
}

function readCached(map: Map<string, CacheEntry>, id: string): LocalRefValidationStatus | null {
  const hit = map.get(id);
  if (!hit) return null;
  if (Date.now() - hit.at > CACHE_TTL_MS) {
    map.delete(id);
    return null;
  }
  return hit.status;
}

function needsLocalValidation(track: any): boolean {
  const id = String(track?.id || track?.trackId || '');
  return isLocalLibraryTrack(track) || isLocalTrackId(id);
}

export function isLocalRecentAlbum(album: Pick<RecentAlbum, 'album_id' | 'source'>): boolean {
  const raw = String(album.album_id || '').trim();
  if (/^local:track:/i.test(raw)) return false;
  if (album.source === 'local_library') return true;
  const id = raw;
  return id.startsWith('local:album:') || /^[0-9a-f]{16}$/i.test(id);
}

async function lookupTrackStatus(trackId: string): Promise<LocalRefValidationStatus> {
  const cached = readCached(trackStatusCache, trackId);
  if (cached) return cached;

  try {
    const resp: any = await libraryService.getTrack(trackId);
    const row = resp?.track || resp;
    if (!row?.id && !row?.file_path && !row?.title) {
      cacheTrackStatus(trackId, 'missing_track');
      return 'missing_track';
    }
    const filePath = row?.file_path || row?.filePath;
    if (!filePath) {
      cacheTrackStatus(trackId, 'missing_file_path');
      return 'missing_file_path';
    }
    cacheTrackStatus(trackId, 'valid');
    return 'valid';
  } catch {
    cacheTrackStatus(trackId, 'missing_track');
    return 'missing_track';
  }
}

async function lookupAlbumStatus(albumId: string): Promise<LocalRefValidationStatus> {
  const normalized = normalizeLocalAlbumId(albumId);
  const cached = readCached(albumStatusCache, normalized);
  if (cached) return cached;

  const detailCached = getCachedLocalAlbumDetail(normalized);
  if (detailCached?.success) {
    const row = detailCached.album;
    if (row?.id || row?.album_key || row?.title) {
      cacheAlbumStatus(normalized, 'valid');
      return 'valid';
    }
  }

  try {
    const resp: any = await libraryService.getAlbum(normalized);
    const row = resp?.album || resp;
    if (!resp?.success && !row?.id && !row?.album_key && !row?.title) {
      cacheAlbumStatus(normalized, 'missing_track');
      return 'missing_track';
    }
    cacheAlbumStatus(normalized, 'valid');
    return 'valid';
  } catch {
    cacheAlbumStatus(normalized, 'missing_track');
    return 'missing_track';
  }
}

async function batchLookupTrackStatuses(ids: string[]): Promise<Map<string, LocalRefValidationStatus>> {
  const out = new Map<string, LocalRefValidationStatus>();
  const pending: string[] = [];

  for (const id of ids) {
    const cached = readCached(trackStatusCache, id);
    if (cached) out.set(id, cached);
    else pending.push(id);
  }

  for (let i = 0; i < pending.length; i += BATCH_SIZE) {
    const chunk = pending.slice(i, i + BATCH_SIZE);
    await Promise.all(chunk.map(async (id) => {
      out.set(id, await lookupTrackStatus(id));
    }));
  }

  return out;
}

async function batchLookupAlbumStatuses(ids: string[]): Promise<Map<string, LocalRefValidationStatus>> {
  const out = new Map<string, LocalRefValidationStatus>();
  const pending: string[] = [];

  for (const id of ids) {
    const normalized = normalizeLocalAlbumId(id);
    const cached = readCached(albumStatusCache, normalized);
    if (cached) out.set(normalized, cached);
    else pending.push(normalized);
  }

  for (let i = 0; i < pending.length; i += BATCH_SIZE) {
    const chunk = pending.slice(i, i + BATCH_SIZE);
    await Promise.all(chunk.map(async (id) => {
      out.set(id, await lookupAlbumStatus(id));
    }));
  }

  return out;
}

/** Annotate local tracks with validation status (streaming tracks → valid). */
export async function validateLocalTrackRefs(
  tracks: any[],
): Promise<Map<string, LocalRefValidationStatus>> {
  const out = new Map<string, LocalRefValidationStatus>();
  if (!shouldValidateLocalLibraryRefs()) {
    for (const t of tracks) {
      const id = String(t?.id || t?.trackId || '');
      if (id) out.set(id, 'valid');
    }
    return out;
  }

  const localIds: string[] = [];
  for (const track of tracks) {
    const id = String(track?.id || track?.trackId || '');
    if (!id) continue;
    if (!needsLocalValidation(track)) {
      out.set(id, 'valid');
      continue;
    }
    localIds.push(id);
  }

  const lookedUp = await batchLookupTrackStatuses(localIds);
  for (const [id, status] of lookedUp) out.set(id, status);
  return out;
}

export async function annotateRecentAlbumsWithValidation(
  albums: RecentAlbum[],
): Promise<RecentAlbum[]> {
  if (!shouldValidateLocalLibraryRefs() || !albums.length) {
    return albums.map((a) => ({ ...a, libraryValidationStatus: 'valid' as const }));
  }

  const localAlbums = albums.filter(isLocalRecentAlbum);
  const ids = localAlbums.map((a) => normalizeLocalAlbumId(a.album_id));
  const statuses = await batchLookupAlbumStatuses(ids);

  const kept: RecentAlbum[] = [];
  for (const album of albums) {
    if (!isLocalRecentAlbum(album)) {
      kept.push({ ...album, libraryValidationStatus: 'valid' as const });
      continue;
    }
    const key = normalizeLocalAlbumId(album.album_id);
    const status = statuses.get(key) || 'missing_track';
    if (status === 'missing_track') {
      purgeStaleLocalAlbumRef(album.album_id);
      continue;
    }
    kept.push({ ...album, libraryValidationStatus: status });
  }
  return kept;
}

export async function sanitizeQueueTracks(
  queue: Array<{ track: any }>,
  queueIndex: number,
  currentTrack: any | null,
): Promise<{
  queue: Array<{ track: any }>;
  queueIndex: number;
  currentTrack: any | null;
  dropped: number;
}> {
  if (!shouldValidateLocalLibraryRefs() || queue.length === 0) {
    return { queue, queueIndex, currentTrack, dropped: 0 };
  }

  const tracks = queue.map((q) => q.track).filter(Boolean);
  const statusMap = await validateLocalTrackRefs(tracks);

  const filtered = queue.filter((item) => {
    const track = item.track;
    if (!track || !needsLocalValidation(track)) return true;
    const id = String(track.id || track.trackId || '');
    if (!id || (!isLocalLibraryTrack(track) && !isLocalTrackId(id))) return true;
    return statusMap.get(id) === 'valid';
  });

  const dropped = queue.length - filtered.length;
  if (dropped === 0) {
    return { queue, queueIndex, currentTrack, dropped: 0 };
  }

  let nextTrack = currentTrack;
  const currentId = currentTrack ? String(currentTrack.id || currentTrack.trackId || '') : '';
  if (
    currentTrack
    && needsLocalValidation(currentTrack)
    && statusMap.get(currentId) !== 'valid'
  ) {
    nextTrack = null;
  }

  let nextIndex = queueIndex;
  if (filtered.length === 0) {
    return { queue: [], queueIndex: 0, currentTrack: null, dropped };
  }

  const priorItem = queue[queueIndex];
  const priorId = priorItem?.track ? String(priorItem.track.id || priorItem.track.trackId || '') : '';
  const remappedIdx = priorId
    ? filtered.findIndex((item) => String(item.track?.id || item.track?.trackId || '') === priorId)
    : -1;

  if (remappedIdx >= 0) {
    nextIndex = remappedIdx;
  } else {
    nextIndex = Math.min(queueIndex, filtered.length - 1);
    nextTrack = filtered[nextIndex]?.track || nextTrack;
  }

  if (!nextTrack) {
    nextTrack = filtered[nextIndex]?.track || filtered[0]?.track || null;
    if (!filtered[nextIndex] && filtered[0]) nextIndex = 0;
  }

  return {
    queue: filtered,
    queueIndex: nextIndex,
    currentTrack: nextTrack,
    dropped,
  };
}

export async function sanitizeMaestroQueueInStore(): Promise<number> {
  const store = useMaestroStore.getState();
  if (!store.queue.length) return 0;

  const result = await sanitizeQueueTracks(store.queue, store.queueIndex, store.currentTrack);
  if (result.dropped === 0) return 0;

  store.setQueue(result.queue, result.queueIndex);
  if (result.currentTrack?.id !== store.currentTrack?.id) {
    store.setCurrentTrack(result.currentTrack);
  }
  if (!result.currentTrack && store.currentStreamUrl) {
    // Stale URI belonged to a removed track — keep stream only if current track still valid.
    store.clearStreamCache();
  }

  console.log(
    `[LocalRefValidator] Removed ${result.dropped} stale queue entr${result.dropped === 1 ? 'y' : 'ies'}`,
  );
  return result.dropped;
}
